import { RadioGroup } from '@headlessui/react'
import clsx from 'clsx'
import { FC, ReactNode } from 'react'

import { cn } from '@/app/utils'

import { RadioGroupOption } from './AppRadioGroup.types'

type Props = {
  className?: string
  wrapperClassNames?: string
  nameContainerClassName?: string
  optionClassName?: string
  optionWrapperClassName?: string
  circleClassName?: string
  options: RadioGroupOption[]
  value?: RadioGroupOption
  nameRenderer?: (option: RadioGroupOption) => ReactNode
  onChange: (option: RadioGroupOption) => void
  onOptionClick?: (option: RadioGroupOption) => void
}

const AppRadioGroup: FC<Props> = ({
  value,
  onChange,
  nameRenderer,
  options,
  className,
  wrapperClassNames,
  nameContainerClassName,
  optionClassName,
  optionWrapperClassName,
  circleClassName,
  onOptionClick
}) => {
  const selectedOption = options.find(option => option.key === value?.key)

  return (
    <RadioGroup
      value={selectedOption}
      className={clsx(className)}
      onChange={onChange}
    >
      <div className={cn('flex flex-col gap-y-12', wrapperClassNames)}>
        {options.map(option => (
          <RadioGroup.Option
            key={option.key}
            value={option}
            disabled={option.disabled}
            className={optionClassName}
            onClick={() => onOptionClick?.(option)}
          >
            {({ checked }) => (
              <div
                className={clsx(
                  'w-full',
                  option.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer'
                )}
              >
                <div
                  className={cn('flex items-center', optionWrapperClassName)}
                >
                  <div
                    className={cn(
                      'h-20 w-20 rounded-full border border-bronze-40',
                      checked && 'border-[6px]',
                      circleClassName
                    )}
                  ></div>

                  <div className={cn('ml-12', nameContainerClassName)}>
                    {(typeof option.name === 'string' || nameRenderer) && (
                      <RadioGroup.Label
                        as="div"
                        className={clsx(
                          'font-pro text-grey-100 text-20 sm:text-16'
                        )}
                      >
                        {nameRenderer?.(option) || (option.name as string)}
                      </RadioGroup.Label>
                    )}

                    {typeof option.name !== 'string' && (
                      <RadioGroup.Label>
                        {(option.name as () => ReactNode)()}
                      </RadioGroup.Label>
                    )}
                  </div>
                </div>
              </div>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}

export default AppRadioGroup
